import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authApi } from "../api/blogApi";

const AuthContext = createContext(null);

// ── JWT payload 디코딩 (검증 없이 exp만 읽음) ────────────────────────────────
const parseTokenExp = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp ? payload.exp * 1000 : null; // ms 단위로 변환
  } catch {
    return null;
  }
};

const isTokenValid = (token) => {
  if (!token) return false;
  const exp = parseTokenExp(token);
  if (!exp) return true; // exp 없으면 일단 유효 처리
  return Date.now() < exp;
};

// ── Provider ─────────────────────────────────────────────────────────────────
export const AuthProvider = ({ children }) => {
  const [isAdmin, setIsAdmin]   = useState(false);
  const [loading, setLoading]   = useState(true);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("admin_token");
    setIsAdmin(false);
  }, []);

  // 앱 시작 시 토큰 유효성 검사
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (isTokenValid(token)) {
      setIsAdmin(true);
    } else if (token) {
      // 만료된 토큰 제거
      localStorage.removeItem("admin_token");
    }
    setLoading(false);
  }, []);

  // axios 인터셉터가 발행하는 401 이벤트 수신 → 자동 로그아웃
  useEffect(() => {
    window.addEventListener("auth:logout", handleLogout);
    return () => window.removeEventListener("auth:logout", handleLogout);
  }, [handleLogout]);

  // 토큰 만료 시 자동 로그아웃 타이머 (서버 auth.js 기준 24h)
  useEffect(() => {
    if (!isAdmin) return;
    const token = localStorage.getItem("admin_token");
    const exp = parseTokenExp(token);
    if (!exp) return;

    const remaining = exp - Date.now();
    if (remaining <= 0) {
      handleLogout();
      return;
    }

    const timer = setTimeout(handleLogout, remaining);
    return () => clearTimeout(timer);
  }, [isAdmin, handleLogout]);

  // 로그인: 비밀번호 → 서버 검증 → 토큰 저장
  const login = async (password) => {
    const { data } = await authApi.login(password);
    localStorage.setItem("admin_token", data.token);
    setIsAdmin(true);
  };

  return (
    <AuthContext.Provider value={{ isAdmin, login, logout: handleLogout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// ── Hook ──────────────────────────────────────────────────────────────────────
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
};